
import React from 'react';
import Layout from '@/components/Layout/Layout';
import LengthConverter from '@/components/UnitConverter/LengthConverter';
import { Link } from 'react-router-dom';

const LengthConverterPage = () => {
  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4">
        {/* Header Section */}
        <div className="mb-8">
          <div className="text-sm text-gray-500 mb-2">
            <Link to="/" className="hover:underline">Home</Link> {' > '} 
            <Link to="/converters" className="hover:underline">Unit Converters</Link> {' > '} 
            <span>Length Converter</span>
          </div>
          <h1 className="text-3xl font-bold mb-3">Length Converter</h1>
          <p className="text-gray-700">
            Convert between different units of length and distance including meters, feet, inches, kilometers, miles, and more. 
            Perfect for construction, engineering, travel planning, and everyday measurements.
          </p>
        </div>

        {/* Converter Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
          <LengthConverter />
        </div>

        {/* Information Section */}
        <div className="bg-[#E6F3FF] p-6 rounded-lg mb-8">
          <h2 className="text-2xl font-semibold mb-3">How to use the Length Converter</h2>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-2">
            <li>Enter the length value you want to convert in the input field</li>
            <li>Select the unit you're converting from using the dropdown menu</li>
            <li>Choose the target unit you want to convert to</li>
            <li>The converted result appears instantly with high precision</li>
            <li>Use the swap button to quickly reverse the conversion direction</li>
          </ol>
        </div>

        {/* Supported Units */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Supported Length Units</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-medium mb-2">Metric System</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2">
                <li><strong>Millimeter (mm):</strong> 1/1000 of a meter</li>
                <li><strong>Centimeter (cm):</strong> 1/100 of a meter</li>
                <li><strong>Meter (m):</strong> Base unit of length in SI system</li>
                <li><strong>Kilometer (km):</strong> 1000 meters</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-medium mb-2">Imperial System</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2">
                <li><strong>Inch (in):</strong> 1/12 of a foot</li>
                <li><strong>Foot (ft):</strong> 12 inches</li>
                <li><strong>Yard (yd):</strong> 3 feet or 36 inches</li>
                <li><strong>Mile (mi):</strong> 5,280 feet</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Common Conversions */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Common Length Conversions</h2>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-medium mb-2">Quick References:</h4>
                <ul className="space-y-1">
                  <li>1 meter = 3.28084 feet</li>
                  <li>1 foot = 0.3048 meters</li>
                  <li>1 inch = 2.54 centimeters</li>
                  <li>1 kilometer = 0.621371 miles</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Practical Examples:</h4>
                <ul className="space-y-1">
                  <li>Room dimensions: feet to meters</li>
                  <li>Height measurements: feet/inches to cm</li>
                  <li>Travel distances: miles to kilometers</li>
                  <li>Sports fields: yards to meters</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Use Cases */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">When to Use the Length Converter</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-2">
            <li><strong>Construction & Engineering:</strong> Convert architectural plans between metric and imperial systems</li>
            <li><strong>Travel Planning:</strong> Convert distances for international trips or navigation</li>
            <li><strong>Sports & Fitness:</strong> Track running distances, pool lengths, and field dimensions</li>
            <li><strong>Shopping & Online Orders:</strong> Understand product dimensions from international sellers</li>
            <li><strong>Education & Science:</strong> Complete homework, lab reports, and research projects</li>
            <li><strong>Home Improvement:</strong> Measure spaces, furniture, and materials accurately</li>
          </ul>
        </div>

        {/* Related Converters */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Related Unit Converters</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/converters/area" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">📐</span> Area Converter
            </Link>
            <Link to="/converters/volume" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">🥛</span> Volume Converter
            </Link>
            <Link to="/converters/weight" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">⚖️</span> Weight Converter
            </Link>
            <Link to="/converters/speed" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">🏃</span> Speed Converter
            </Link>
            <Link to="/converters/temperature" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">🌡️</span> Temperature Converter
            </Link>
            <Link to="/special/percentage" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">%</span> Percentage Calculator
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LengthConverterPage;
